"use client"

import { usePathname, useRouter } from "next/navigation" // <--- Importuri noi critice
import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Languages, Check } from "lucide-react"
import { usePreferences, type LanguageType } from "@/contexts/preferences-context"
import { i18n } from "@/i18n-config"

interface LanguageSwitcherProps {
    currentLang: string;
    availableLanguages?: LanguageType[];
}

export function LanguageSwitcher({ currentLang, availableLanguages }: LanguageSwitcherProps) {
    const pathname = usePathname() // Citim calea curentă (ex: /ro/dashboard)
    const router = useRouter()     // Folosim routerul pentru navigare fără refresh complet
    const { preferences, updatePreferences } = usePreferences()
    const pathSegments = pathname?.split("/") ?? []
    const routeLang = i18n.locales.find((locale) => locale === pathSegments[1])
    const fallbackLang = i18n.locales.find((locale) => locale === currentLang) ?? i18n.defaultLocale
    const isDashboardRoute = pathSegments[2] === "dashboard"
    const activeLang = isDashboardRoute
        ? (preferences.language ?? routeLang ?? fallbackLang)
        : (routeLang ?? preferences.language ?? fallbackLang)
    const triggerId = "header-language-menu-trigger"
    const contentId = "header-language-menu-content"
    const languages = availableLanguages ?? ["ro", "en", "de"]

    const handleLanguageChange = (newLang: LanguageType) => {
        if (!pathname) return "/"

        if (newLang === activeLang) {
            return
        }

        // 1. Spargem calea în segmente: ["", "ro", "dashboard", "buyer"]
        const segments = pathname.split("/")

        // 2. Reconstruim calea: "/en/dashboard/buyer" fără mutații
        const newPath = segments
            .map((segment, index) => (index === 1 ? newLang : segment))
            .join("/")

        // 4. Actualizam limba globala instant
        updatePreferences({ language: newLang })

        // 5. In dashboard evitam remount-ul de ruta si doar actualizam URL-ul in bara de adrese.
        const isDashboardNavigation = segments[2] === "dashboard"
        if (isDashboardNavigation) {
            window.history.replaceState(window.history.state, "", newPath)
            return
        }

        // Pentru paginile publice/auth păstrăm navigarea standard (dict de pe server).
        router.push(newPath)
    }

    const getFlag = (lang: string) => {
        switch (lang) {
            case "ro": return "🇷🇴";
            case "en": return "🇬🇧";
            case "de": return "🇩🇪";
            default: return "🇷🇴";
        }
    }

    const getLabel = (lang: LanguageType) => {
        switch (lang) {
            case "ro": return "Română"
            case "en": return "English"
            case "de": return "Deutsch"
            default: return "Romana"
        }
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button id={triggerId} variant="ghost" size="sm" className="gap-2 px-2">
                    <Languages className="h-4 w-4" />
                    <span>{getFlag(activeLang)}</span>
                    <span className="hidden sm:inline uppercase">{activeLang}</span>
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent id={contentId} aria-labelledby={triggerId} align="end">
                {languages.map((lang) => (
                    <DropdownMenuItem key={lang} onClick={() => handleLanguageChange(lang)} className="cursor-pointer">
                        <span className="mr-2">{getFlag(lang)}</span> {getLabel(lang)}
                        {activeLang === lang && <Check className="ml-auto h-4 w-4" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}